import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { IsoTimestamp, formatTimeRange, formatToTimeZone } from '@modernloop/shared/datetime';
import { EyeCrossedIcon, LinkIcon } from '@modernloop/shared/icons';
import { GoogleMeetLogo, MicrosoftTeamsLogo, ZoomLogo } from '@modernloop/shared/images';
import { Avatar, Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';

import {
  ApplicationStatus,
  ExternalObjectSyncState,
  ScheduleContentInterviewHeader_CandidateEventFragment,
  ScheduleContentInterviewHeader_InterviewFragment,
} from 'src/generated/mloop-graphql';

import { useOrgAtsService } from 'src/hooks/atsService';
import { getCollaborationService } from 'src/hooks/collaborationService';

import ScheduleContentInterviewMenu from './ScheduleContentInterviewMenu';

type Fragments = {
  interview: ScheduleContentInterviewHeader_InterviewFragment;
  candidateEvent: ScheduleContentInterviewHeader_CandidateEventFragment | undefined;
};

type Props = {
  applicationStageId: string;
  applicationStatus: ApplicationStatus;
  timezone: string;
  frozenAt?: IsoTimestamp;
};

type AtsSyncStatusIconProps = {
  atsLogoUrl: string;
  atsName: string;
  errorCode?: string | null;
  errorMessage?: string | null;
  interviewName?: string | null;
  /**
   * Legacy fallback (no tracked sync status): the interview is stitched to an ATS interview but
   * has no ATS scheduled interview id, so we know the ATS booking link is broken.
   */
  isLegacyUnlinked?: boolean;
  isRetryExhausted?: boolean;
  /** Undefined in the legacy fallback, where the tooltip says nothing about sync status. */
  state?: ExternalObjectSyncState;
  syncedAt?: string | null;
  timezone: string;
};

const AtsSyncStatusIcon = ({
  atsLogoUrl,
  atsName,
  errorCode,
  errorMessage,
  interviewName,
  isLegacyUnlinked,
  isRetryExhausted,
  state,
  syncedAt,
  timezone,
}: AtsSyncStatusIconProps) => {
  const { t } = useTranslation('hiring-dashboard-schedule');

  const marker = (() => {
    if (state === ExternalObjectSyncState.Pending) {
      return (
        <CircularProgress
          data-testid="schedule-content-interview-ats-sync-pending-spinner"
          size={10}
          thickness={5}
          sx={{ color: 'primary.main', flexShrink: 0 }}
        />
      );
    }
    if (state === ExternalObjectSyncState.Failed || isLegacyUnlinked) {
      return (
        <Box
          data-testid="schedule-content-interview-ats-sync-failed-dot"
          sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }}
        />
      );
    }
    return null;
  })();

  const isError = state === ExternalObjectSyncState.Failed || Boolean(isLegacyUnlinked);

  const caption = (() => {
    if (state === undefined) {
      // Legacy fallback: only call out the known-broken link; otherwise stay silent about sync.
      return isLegacyUnlinked ? t('schedule.content.externalSync.notLinked', { atsName }) : null;
    }
    if (state === ExternalObjectSyncState.Failed) {
      return isRetryExhausted
        ? t('schedule.content.externalSync.failedRetriesStopped', { atsName })
        : t('schedule.content.externalSync.failed', { atsName });
    }
    if (state === ExternalObjectSyncState.Pending) {
      return t('schedule.content.externalSync.pending', { atsName });
    }
    if (syncedAt) {
      return t('schedule.content.externalSync.syncedAt', {
        datetime: formatToTimeZone(syncedAt as IsoTimestamp, 'MMM d, h:mm a zzz', timezone),
      });
    }
    return t('schedule.content.externalSync.synced');
  })();

  // Verbatim provider error — deliberately not localized: it is exactly what the ATS reported.
  const providerError = (() => {
    if (state !== ExternalObjectSyncState.Failed) {
      return null;
    }
    if (errorCode && errorMessage) {
      return `${errorCode}: ${errorMessage}`;
    }
    return errorMessage || errorCode || null;
  })();

  return (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          {interviewName ? (
            <Typography
              variant="subtitle2"
              color="inherit"
              data-testid="schedule-content-interview-ats-sync-interview-name"
            >
              {interviewName}
            </Typography>
          ) : null}
          <Typography variant="caption" color="inherit">
            {t('schedule.content.externalSync.tooltipTitle', { atsName })}
          </Typography>
          {caption ? (
            <Typography
              variant="caption"
              color={isError ? 'error.light' : 'inherit'}
              data-testid="schedule-content-interview-ats-sync-caption"
            >
              {caption}
            </Typography>
          ) : null}
          {providerError ? (
            <Typography
              variant="caption"
              color="error.light"
              data-testid="schedule-content-interview-ats-sync-provider-error"
            >
              {providerError}
            </Typography>
          ) : null}
        </Stack>
      }
    >
      <Box
        data-testid="schedule-content-interview-ats-sync-icon"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, justifyContent: 'center' }}
      >
        <Avatar src={atsLogoUrl} sx={{ width: '16px', height: '16px' }} />
        {marker}
      </Box>
    </Tooltip>
  );
};

const ScheduleContentInterviewHeader: FCWithFragments<Fragments, Props> = ({
  applicationStageId,
  applicationStatus,
  interview,
  candidateEvent,
  timezone,
  frozenAt,
}) => {
  const { t } = useTranslation('hiring-dashboard-schedule');
  const atsService = useOrgAtsService();

  const trackedSyncStatus =
    interview.atsSyncStatus && interview.atsSyncStatus.state !== ExternalObjectSyncState.NoConnection
      ? interview.atsSyncStatus
      : null;

  // Legacy fallback for interviews with no tracked sync status: the ATS icon still renders when
  // the interview is stitched to an ATS interview. A missing ATS scheduled interview id in that
  // case is a known-broken booking link, surfaced like a sync error.
  const showLegacyAtsIcon = !trackedSyncStatus && Boolean(interview.atsInterviewDefinitionId);

  const atsSyncIcon =
    atsService && (trackedSyncStatus || showLegacyAtsIcon) ? (
      <AtsSyncStatusIcon
        atsLogoUrl={atsService.logoUrl}
        atsName={atsService.name}
        errorCode={trackedSyncStatus?.errorCode}
        errorMessage={trackedSyncStatus?.errorMessage}
        interviewName={interview.name}
        isLegacyUnlinked={showLegacyAtsIcon && !interview.atsScheduledInterviewId}
        isRetryExhausted={trackedSyncStatus?.isRetryExhausted}
        state={trackedSyncStatus?.state}
        syncedAt={trackedSyncStatus?.syncedAt}
        timezone={timezone}
      />
    ) : null;

  const locationIcon = useMemo(() => {
    const videoMeetingLink = interview.meetingLocation?.videoMeetingLink || interview.videoMeetingUrl;
    if (videoMeetingLink?.toLowerCase().includes('zoom')) {
      return (
        <Tooltip title={t('schedule.content.zoom')}>
          <Avatar
            data-testid="schedule-content-interview-meeting-zoom-icon"
            src={ZoomLogo}
            sx={{ width: '16px', height: '16px' }}
          />
        </Tooltip>
      );
    }

    if (videoMeetingLink?.toLowerCase().includes('meet.google.com')) {
      return (
        <Tooltip title={t('schedule.content.googleMeets')}>
          <Avatar
            data-testid="schedule-content-interview-meeting-google-meets-icon"
            src={GoogleMeetLogo}
            sx={{ width: '16px', height: '16px' }}
          />
        </Tooltip>
      );
    }

    if (
      videoMeetingLink?.toLowerCase().includes('teams.microsoft.com') ||
      videoMeetingLink?.toLowerCase().includes('teams.microsoft.us')
    ) {
      return (
        <Tooltip title={t('schedule.content.microsoftTeams')}>
          <Avatar
            data-testid="schedule-content-interview-meeting-microsoft-teams-icon"
            src={MicrosoftTeamsLogo}
            sx={{ width: '16px', height: '16px' }}
          />
        </Tooltip>
      );
    }

    if (videoMeetingLink?.length) {
      return (
        <LinkIcon
          data-testid="schedule-content-interview-meeting-custom-link-icon"
          tooltip={t('schedule.content.includesCustomMeetingLink')}
          fontSize="small"
        />
      );
    }

    return null;
  }, [interview.meetingLocation?.videoMeetingLink, interview.videoMeetingUrl, t]);

  const codingIntegrationIcon = useMemo(() => {
    const codingUrl = interview.codingInterviewUrl;

    if (!codingUrl && !interview.collaborationLink) {
      return null;
    }

    let tooltip = '';
    let iconUrl = '';

    if (interview.collaborationLink) {
      const collaborationToolService = getCollaborationService(interview.collaborationLink.collaborationToolType);
      if (!collaborationToolService) {
        return null;
      }
      tooltip = collaborationToolService.name;
      iconUrl = collaborationToolService.logoUrl;
    } else if (codingUrl && codingUrl.indexOf('coderpad') !== -1) {
      tooltip = 'CoderPad';
      iconUrl = '/static/images/integrations/CoderPad.jpeg';
    } else if (codingUrl && codingUrl.indexOf('codesignal') !== -1) {
      tooltip = 'CodeSignal';
      iconUrl = '/static/images/integrations/codesignal.png';
    } else if (codingUrl && codingUrl.indexOf('codility') !== -1) {
      // Example Codility link - https://app.codility.com/test/XYZ123/,
      tooltip = 'Codility';
      iconUrl = '/static/images/integrations/codility.png';
    } else if (codingUrl && codingUrl.indexOf('hr.gs') !== -1) {
      // Example HackerRank link - https://hr.gs/123456
      tooltip = 'HackerRank';
      iconUrl = '/static/images/integrations/hackerrank.png';
    }

    return (
      <Tooltip title={tooltip}>
        <Avatar
          data-testid="schedule-content-interview-coding-link-icon"
          src={iconUrl}
          sx={{ width: (theme) => theme.spacing(2), height: (theme) => theme.spacing(2) }}
        />
      </Tooltip>
    );
  }, [interview.codingInterviewUrl, interview.collaborationLink]);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" pl={2.5} pr={1.5} py={0.5}>
      <Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {interview.isHiddenFromCandidate && (
            <EyeCrossedIcon
              data-testid="schedule-content-interview-header-hidden-interview-icon"
              tooltip={t('schedule.content.hiddenFromCandidate')}
            />
          )}
          <Typography
            variant="body1"
            data-testid="schedule-content-interview-header-interview-name"
            data-interview-name={interview.name}
          >
            {interview.name}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {formatTimeRange(interview.startAt, interview.endAt, timezone, true /* showTzAbbr */)}
          </Typography>

          {locationIcon}

          {atsSyncIcon}

          {codingIntegrationIcon}
        </Stack>
      </Stack>

      <ScheduleContentInterviewMenu
        applicationStatus={applicationStatus}
        applicationStageId={applicationStageId}
        interview={interview}
        candidateEvent={candidateEvent}
        frozenAt={frozenAt}
      />
    </Stack>
  );
};

ScheduleContentInterviewHeader.fragments = {
  interview: gql`
    ${ScheduleContentInterviewMenu.fragments.interview}
    fragment ScheduleContentInterviewHeader_interview on ApplicationStageInterview {
      id
      name
      startAt
      endAt
      googleEventUrl
      videoMeetingUrl
      zoomDialInInfo
      atsInterviewDefinitionId
      atsScheduledInterviewId
      atsSyncStatus {
        id
        providerType
        state
        syncedAt
        errorCode
        errorMessage
        isRetryExhausted
        attemptCount
      }
      codingInterviewUrl
      collaborationLink {
        id
        collaborationToolType
      }
      isHiddenFromCandidate
      ...ScheduleContentInterviewMenu_interview
    }
  `,
  candidateEvent: gql`
    ${ScheduleContentInterviewMenu.fragments.candidateEvent}
    fragment ScheduleContentInterviewHeader_candidateEvent on ApplicationStageCandidateEvent {
      id
      ...ScheduleContentInterviewMenu_candidateEvent
    }
  `,
};

export default ScheduleContentInterviewHeader;
